import React from 'react';
import { Card, Space, Typography } from 'antd';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { useTranslation } from '../../hooks/useTranslation';
import dayjs from 'dayjs';

const { Text } = Typography;

export interface TimelineItem {
  key: string;
  icon: React.ReactNode;
  title: string;
  timestamp?: string | null;
  color?: string;
  description?: string;
}

export interface AppointmentTimelineProps {
  appointment: {
    created_at: string;
    confirmed_at?: string | null;
    started_at?: string | null;
    completed_at?: string | null;
    cancelled_at?: string | null;
  };
  title?: string;
  style?: React.CSSProperties;
  customItems?: TimelineItem[];
  showDefaultItems?: boolean;
  dateFormat?: string;
}

const AppointmentTimeline: React.FC<AppointmentTimelineProps> = ({
  appointment,
  title,
  style = { marginBottom: 24 },
  customItems = [],
  showDefaultItems = true,
  dateFormat = 'MMM DD, YYYY HH:mm'
}) => {
  const { t } = useTranslation();

  const defaultTimelineItems: TimelineItem[] = [
    {
      key: 'created',
      icon: <CalendarOutlined />,
      title: t('appointments.created'),
      timestamp: appointment.created_at,
      color: undefined,
    },
    {
      key: 'confirmed',
      icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      title: t('appointments.confirmed'),
      timestamp: appointment.confirmed_at,
      color: '#52c41a',
    },
    {
      key: 'started',
      icon: <SyncOutlined style={{ color: '#fa8c16' }} />,
      title: t('appointments.started'),
      timestamp: appointment.started_at,
      color: '#fa8c16',
    },
    {
      key: 'completed',
      icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      title: t('appointments.completed'),
      timestamp: appointment.completed_at,
      color: '#52c41a',
    },
    {
      key: 'cancelled',
      icon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
      title: t('appointments.cancelled'),
      timestamp: appointment.cancelled_at,
      color: '#ff4d4f',
    },
  ];

  // Combine custom items with default items if both are enabled
  const timelineItems = showDefaultItems 
    ? [...defaultTimelineItems, ...customItems]
    : customItems;

  return (
    <Card title={title || t('appointments.timeline')} style={style}>
      <div style={{ padding: '8px 0' }}>
        {timelineItems.map((item) => {
          if (!item.timestamp) return null;
          
          return (
            <div key={item.key} style={{ marginBottom: 16 }}>
              <Space>
                {item.icon}
                <div>
                  <Text strong>{item.title}</Text>
                  <br />
                  <Text type="secondary">
                    {dayjs(item.timestamp).format(dateFormat)}
                  </Text>
                  {item.description && (
                    <>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {item.description}
                      </Text>
                    </>
                  )}
                </div>
              </Space>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default AppointmentTimeline;
